import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { map, switchMap, catchError, tap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { defer, of } from 'rxjs';

import * as authActions from '../actions/auth.actions';
import * as fromServices from '@modules/auth/services/';
import { ClientCredentials } from '@modules/auth/interfaces';

@Injectable()
export class AuthEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly router: Router,
        private readonly authService: fromServices.AuthService,
        private readonly parse: fromServices.ParseService,
        private readonly supabase: fromServices.SupabaseService
    ) {}

    logInWithLocal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(authActions.LogInWithLocal),
            map((action) => action.payload),
            switchMap((clientCredentials: ClientCredentials) => {
                return defer(() => this.authService.login(clientCredentials)).pipe(
                    map((payload) => ({
                        type: authActions.LOGIN_WITH_LOCAL_SUCCESS,
                        payload
                    })),
                    catchError((error) => of(authActions.LogInWithLocalFail(error)))
                );
            })
        );
    });

    logIn$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(authActions.LogIn),
            map((action) => action.payload),
            switchMap((clientCredentials: ClientCredentials) => {
                return defer(() => this.parse.logIn(clientCredentials.email, clientCredentials.password)).pipe(
                    map((payload) => ({
                        type: authActions.LOGIN_SUCCESS,
                        payload
                    })),
                    catchError((error) => of(authActions.LogInFail(error)))
                );
            })
        );
    });

    logInWithSession$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(authActions.LogInWithSession),
            map((action) => action.payload),
            switchMap((sessionToken: string) => {
                return defer(() => this.parse.become(sessionToken)).pipe(
                    map((payload) => ({
                        type: authActions.LOGIN_WITH_SESSION_SUCCESS,
                        payload
                    })),
                    catchError((error) => of(authActions.LogInFail(error)))
                );
            })
        );
    });

    logInWithSupabase$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(authActions.LogInWithSupabase),
            map((action) => action.payload),
            switchMap((userCredentials: any) => {
                return defer(() => this.supabase.signIn(userCredentials.email, userCredentials.password)).pipe(
                    map((payload) => ({
                        type: authActions.LOGIN_WITH_SUPABASE_SUCCESS,
                        payload
                    })),
                    catchError((error) => of(authActions.LogInFail(error)))
                );
            })
        );
    });

    logInSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    authActions.LOGIN_WITH_LOCAL_SUCCESS,
                    authActions.LOGIN_SUCCESS,
                    authActions.LOGIN_WITH_SESSION_SUCCESS,
                    authActions.LOGIN_WITH_SUPABASE_SUCCESS
                ),
                tap(() => {
                    this.router.navigate(['/dashboard']);
                })
            ),
        { dispatch: false }
    );

    logOutWithLocal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.LOGOUT_WITH_LOCAL),
                tap(async () => {
                    await this.authService.logOut();
                    localStorage.clear();
                    this.router.navigate(['/login']);
                })
            ),
        { dispatch: false }
    );

    logOut$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.LOGOUT),
                tap(async () => {
                    await this.parse.logOut();
                    localStorage.clear();
                    this.router.navigate(['/login']);
                })
            ),
        { dispatch: false }
    );

    logOutWithSupabase$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.LOGOUT_WITH_SUPABASE),
                tap(async () => {
                    await this.supabase.signOut();
                    localStorage.clear();
                    this.router.navigate(['/login']);
                })
            ),
        { dispatch: false }
    );
}
